import { Swiper } from 'swiper';
import { A11y, Autoplay, Navigation, Pagination, Scrollbar, Thumbs } from 'swiper/modules';
import { Fancybox } from '@fancyapps/ui';

if (!customElements.get('product-media-slider')) {
  class ProductMediaSlider extends HTMLElement {
    constructor() {
      super();
      // Get navigation buttons
      this.navigation = this.querySelectorAll('[data-swiper-nav-button]');

      // Get product data
      this.productData = JSON.parse(this.dataset.product) || {};

      // State for the current slide
      this.currentSlide = 0;
      this.currentSlideLightbox = 0;

      // Check if thumbnails are enabled
      this.thumbnailsEnabledDesktop = this.dataset.enableThumbnailsDesktop === 'true';
      this.thumbnailsEnabledMobile = this.dataset.enableThumbnailsMobile === 'true';
      this.thumbnailsEnabled = this.thumbnailsEnabledDesktop || this.thumbnailsEnabledMobile;
      
      // Track event listeners for cleanup
      this.thumbnailListeners = [];
      
      // Bind resize handler for cleanup
      this.handleResize = this.handleResize.bind(this);

      // Create elements
      if (this.thumbnailsEnabled) {
        this.thumbnailSlider();
      }
      this.mainSlider();
      this.fancybox();

      // Reduce actions (So the event can also be removed)
      this.reducer = {
        slideToVariant: (e) => this.slideToVariant(e),
      };

      // Listen for variant change and go to slide
      document.addEventListener('variant-change', this.reducer.slideToVariant);
      
      // Listen for resize to update overlay visibility
      if (this.thumbnailsEnabled) {
        window.addEventListener('resize', this.handleResize);
      }
    }

    connectedCallback() {
      // Define variables for timer and timeout duration
      let timer;
      let timeOut = parseInt(this.dataset.resetTime) || 3000;

      // Function to hide navigation
      const hideNavigation = () => {
        document.body.classList.add('swiper-engaged');
      };

      // Function to reset timer and show navigation
      const resetTimer = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          document.body.classList.remove('swiper-engaged');
        }, timeOut);
      };

      // Event listener for slide change transition start
      this.swiperMainInstance.on('slideChangeTransitionStart', () => {
        // Hide navigation
        hideNavigation();
        // Reset timer to show navigation after timeout
        resetTimer();
      });
    }

    /*
     * Create thumbnail slider
     */
    thumbnailSlider() {
      // Check if Swiper exists
      if (typeof Swiper == 'undefined') {
        return false;
      }

      // Get desktop swiper element
      this.swiperThumbsDesktop = this.thumbnailsEnabledDesktop ? this.querySelector('[data-product-media-thumbs="desktop"]') : null;
      
      // Get mobile swiper element (might be outside this component)
      // For now, use document query but we could improve this with better data attributes later
      this.swiperThumbsMobile = this.thumbnailsEnabledMobile ? document.querySelector('[data-product-media-thumbs="mobile"]') : null;
      
      if (!this.swiperThumbsDesktop && !this.swiperThumbsMobile) {
        return false;
      }

      // Swiper thumbs options
      this.swiperThumbsOptions = {
        modules: [A11y, Navigation],
        spaceBetween: 12,
        slidesPerView: 6,
        freeMode: true,
        watchSlidesProgress: true,
      };

      // Create Desktop Thumbs slider
      if (this.swiperThumbsDesktop) {
        this.swiperThumbsInstance = new Swiper(this.swiperThumbsDesktop, this.swiperThumbsOptions);
        
        // Handle desktop "+X" indicator visibility on slide change
        if (this.swiperThumbsInstance) {
          this.swiperThumbsInstance.on('slideChange', () => {
            this.updatePlusXOverlay();
          });
          
          // Initialize overlay after slider is ready
          this.swiperThumbsInstance.on('init', () => {
            this.updatePlusXOverlay();
          });
        }
      }

      // Create Mobile Thumbs slider
      if (this.swiperThumbsMobile) {
        this.swiperThumbsMobileInstance = new Swiper(this.swiperThumbsMobile, {
          modules: [A11y],
          slidesPerView: 5,
          spaceBetween: 8,
          freeMode: true,
          watchSlidesProgress: true,
        });

        // Handle mobile "+X" indicator visibility on slide change
        if (this.swiperThumbsMobileInstance) {
          this.swiperThumbsMobileInstance.on('slideChange', () => {
            this.updatePlusXOverlay();
          });
          
          // Initialize overlay after slider is ready
          this.swiperThumbsMobileInstance.on('init', () => {
            this.updatePlusXOverlay();
          });
        }
      }

      // Add click event to thumbnail buttons within this component's scope
      const desktopThumbButtons = this.swiperThumbsDesktop ? 
        this.swiperThumbsDesktop.querySelectorAll('[data-thumb-index]') : [];
      const mobileThumbButtons = this.swiperThumbsMobile ? 
        this.swiperThumbsMobile.querySelectorAll('[data-thumb-index]') : [];
      
      // Combine both desktop and mobile buttons
      const allThumbButtons = [...desktopThumbButtons, ...mobileThumbButtons];
      
      // Add click handlers and store references for cleanup
      allThumbButtons.forEach((button) => {
        const handler = (e) => {
          const index = parseInt(e.currentTarget.dataset.thumbIndex);
          if (this.swiperMainInstance) {
            // Use slideToLoop for better loop handling
            this.swiperMainInstance.slideToLoop(index);
          }
        };
        
        button.addEventListener('click', handler);
        this.thumbnailListeners.push({ element: button, handler });
      });
    }

    /*
     * Create main slider
     */
    mainSlider() {
      // Check if Swiper exists
      if (typeof Swiper == 'undefined') {
        return false;
      }

      // Get swiper element
      this.swiperMain = this.querySelector('[data-product-media-slider]');

      // Get initial slide and swiper index - always start at 0
      let initialSlideIndex = 0;

      // Swiper options
      const modules = [A11y, Autoplay, Navigation, Pagination, Scrollbar];
      if (this.thumbnailsEnabled) {
        modules.push(Thumbs);
      }

      this.swiperMainOptions = {
        modules: modules,
        threshold: 10,
        loop: true,
        direction: 'horizontal',
        initialSlide: initialSlideIndex,
        Pagination: false,
        Scrollbar: false,
        navigation: {
          prevEl: this.querySelector('.product-media-slider__button-prev'),
          nextEl: this.querySelector('.product-media-slider__button-next'),
        },
      };

      // Add thumbs configuration if enabled
      if (this.thumbnailsEnabled && this.swiperThumbsInstance) {
        this.swiperMainOptions.thumbs = {
          swiper: this.swiperThumbsInstance,
        };
      }

      // Create Main slider
      this.swiperMainInstance = new Swiper(this.swiperMain, this.swiperMainOptions);
      
      // Set initial active thumbnail
      this.updateActiveThumbnail(initialSlideIndex);

      // Add event listener for slide change to send data to Elevar
      this.swiperMainInstance.on('slideChange', (swiper) => {
        if (this.currentSlide === swiper.activeIndex) {
          return;
        }
        this.currentSlide = swiper.activeIndex;
        
        // Update active thumbnail state for both mobile and desktop
        // Use realIndex for proper loop handling
        this.updateActiveThumbnail(swiper.realIndex);
        
        if (window.ElevarDataLayer) {
          window.ElevarDataLayer.push({
            event: 'slide_change_pdp',
            slide: this.currentSlide,
            product: this.productData,
          });
        }
      });

      // Rebuid Swiper in design mode
      if (Shopify.designMode && this.swiperMainInstance) {
        window.addEventListener('shopify:section:load', () => {
          this.swiperMainInstance.update();
        });
      }
    }

    /*
     * Create fancybox
     */
    fancybox() {
      // Check if Fancybox exists
      if (typeof Swiper == 'undefined') {
        return false;
      }

      Fancybox.bind('[data-fancybox]', {
        animated: false,
        showClass: false,
        mainClass: 'image-zoom',
        // Listen for slide change to send data to Elevar
        on: {
          init: (fancybox, slide) => {
            window.ElevarDataLayer.push({
              event: 'open_lightbox',
              slide: this.currentSlideLightbox,
              product: this.productData,
            });
          },
          'Carousel.settle': (fancybox, slide) => {
            if (this.currentSlideLightbox === slide.pageIndex) {
              return;
            }
            this.currentSlideLightbox = slide.pageIndex;
            if (window.ElevarDataLayer) {
              window.ElevarDataLayer.push({
                event: 'slide_change_lightbox',
                slide: this.currentSlideLightbox,
                product: this.productData,
              });
            }
          },
        },
      });
    }

    /*
     * Slide to variant
     * @Param e {object}: event object with variant
     */
    slideToVariant(e) {
      if (e?.detail?.variant?.id) {
        const slideIndex = this.matchVariantIdWithSlide(e.detail.variant.id);
        if (typeof slideIndex == 'number') {
          this.goToSlide(slideIndex);
        }
      }
    }

    /*
     * Match variant id with slide
     * @param id {int}: variant id
     */
    matchVariantIdWithSlide(id) {
      if (!id || !this.swiperMain) {
        return false;
      }

      // Get slides
      const slides = this.swiperMain.querySelectorAll('[data-product-variant-image]');

      // Match id with slides id's
      const slide = [...slides].find((slide) => {
        // Get variant id's attached to slide
        const slideVariants = JSON.parse(slide.dataset.productVariantImage);

        // Check if variant id match the slide variant id's
        if (slideVariants.includes(id)) {
          return slide;
        }
      });

      // return false if no slide found
      if (!slide) {
        return false;
      }

      // return matched slide index
      return getNodeIndex(slide);
    }

    /*
     * go to slide
     * @param slide {node} / {int}: slide node / index
     */
    goToSlide(slide) {
      if ((typeof slide != 'number' && typeof slide != 'object') || !this.swiperMainInstance) {
        return false;
      }

      // Get index, transform node to slide index or string to int
      const index = typeof slide == 'number' ? parseInt(slide) : getNodeIndex(slide);

      // go to slide
      this.swiperMainInstance.slideTo(index);
    }


    /*
     * Update active thumbnail
     * @param index {int}: active slide index
     */
    updateActiveThumbnail(index) {
      // Update thumbnail sliders to show active state
      // Use realIndex for proper synchronization with main slider's loop
      if (this.swiperThumbsInstance) {
        this.swiperThumbsInstance.slideTo(index, 300, false);
      }
      if (this.swiperThumbsMobileInstance) {
        this.swiperThumbsMobileInstance.slideTo(index, 300, false);
      }

      // Update active class on thumbnail buttons - scoped to this component
      const desktopThumbs = this.swiperThumbsDesktop ? 
        this.swiperThumbsDesktop.querySelectorAll('[data-thumb-index]') : [];
      const mobileThumbs = this.swiperThumbsMobile ? 
        this.swiperThumbsMobile.querySelectorAll('[data-thumb-index]') : [];
      
      // Update active states for both desktop and mobile thumbnails
      [...desktopThumbs, ...mobileThumbs].forEach((thumb) => {
        const thumbIndex = parseInt(thumb.dataset.thumbIndex);
        const isActive = thumbIndex === index;
        
        // Toggle active class
        thumb.classList.toggle('is-active', isActive);
        
        // Only handle swiper slide class if it's in a swiper slide
        if (thumb.parentElement && thumb.parentElement.classList.contains('swiper-slide')) {
          thumb.parentElement.classList.toggle('swiper-slide-thumb-active', isActive);
        }
      });
      
      // Update +X overlay visibility
      this.updatePlusXOverlay();
    }

    /*
     * Update +X overlay visibility based on current thumbnail position
     */
    updatePlusXOverlay() {
      // Handle desktop thumbnails
      if (this.swiperThumbsInstance) {
        this.updateOverlayForSlider(this.swiperThumbsInstance, 'desktop');
      }
      
      // Handle mobile thumbnails
      if (this.swiperThumbsMobileInstance) {
        this.updateOverlayForSlider(this.swiperThumbsMobileInstance, 'mobile');
      }
    }

    /*
     * Update overlay for a specific slider instance
     */
    updateOverlayForSlider(swiperInstance, device) {
      const totalSlides = swiperInstance.slides.length;
      const slidesPerView = swiperInstance.params.slidesPerView || (device === 'desktop' ? 6 : 5);
      const activeIndex = swiperInstance.activeIndex || 0;
      
      // Clear existing overlays
      swiperInstance.slides.forEach(slide => {
        const existingOverlay = slide.querySelector('.thumb-overlay-plus');
        if (existingOverlay) {
          existingOverlay.remove();
        }
      });
      
      // Calculate visible range
      const visibleEnd = activeIndex + slidesPerView;
      const hasMoreAfterVisible = totalSlides > visibleEnd;
      
      // Add overlay to the last visible slide if there are more images
      if (hasMoreAfterVisible) {
        const lastVisibleIndex = Math.min(visibleEnd - 1, totalSlides - 1);
        const lastVisibleSlide = swiperInstance.slides[lastVisibleIndex];
        const remainingCount = totalSlides - visibleEnd;
        
        if (lastVisibleSlide) {
          const overlay = document.createElement('div');
          overlay.className = 'thumb-overlay-plus absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 rounded transition-opacity duration-200';
          overlay.innerHTML = `<span class="text-white font-bold ${device === 'desktop' ? 'text-sm' : 'text-base'}">+${remainingCount}</span>`;
          
          const thumbButton = lastVisibleSlide.querySelector('.product-media-slider__thumb');
          if (thumbButton) {
            thumbButton.appendChild(overlay);
          }
        }
      }
    }

    /*
     * Handle window resize to update overlay visibility
     */
    handleResize() {
      // Debounce resize events
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        if (this.thumbnailsEnabled) {
          this.updatePlusXOverlay();
        }
      }, 150);
    }

    /*
     * Clean up when component is removed from DOM
     */
    disconnectedCallback() {
      // Remove thumbnail click listeners
      this.thumbnailListeners.forEach(({ element, handler }) => {
        element.removeEventListener('click', handler);
      });
      this.thumbnailListeners = [];
      
      // Remove variant change listener
      document.removeEventListener('variant-change', this.reducer.slideToVariant);
      
      // Remove resize listener
      window.removeEventListener('resize', this.handleResize);
      
      // Clear any pending resize timeout
      clearTimeout(this.resizeTimeout);
      
      // Destroy Swiper instances
      if (this.swiperMainInstance) {
        this.swiperMainInstance.destroy(true, true);
        this.swiperMainInstance = null;
      }
      
      if (this.swiperThumbsInstance) {
        this.swiperThumbsInstance.destroy(true, true);
        this.swiperThumbsInstance = null;
      }
      
      if (this.swiperThumbsMobileInstance) {
        this.swiperThumbsMobileInstance.destroy(true, true);
        this.swiperThumbsMobileInstance = null;
      }
    }
  }

  window.ProductMediaSlider = ProductMediaSlider;

  customElements.define('product-media-slider', ProductMediaSlider);
}
